import React, { useEffect, useState } from "react";
import { Sparkles, Wand2 } from "lucide-react";
import { api } from "../api";
import { BookCover } from "./BookCover";
import { Link } from "react-router-dom";

export function QuizSection() {
  const [quiz, setQuiz] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/quiz").then((r) => setQuiz(r.data)).catch(() => {});
  }, []);

  if (!quiz) return null;

  const current = quiz.questions[step];

  const choose = async (value) => {
    const next = { ...answers, [current.id]: value };
    setAnswers(next);
    if (step < quiz.questions.length - 1) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        const { data } = await api.post("/quiz/recommend", next);
        setResults(data.results);
      } finally {
        setLoading(false);
      }
    }
  };

  const reset = () => { setStep(0); setAnswers({}); setResults(null); };

  return (
    <div className="glass-panel relative overflow-hidden rounded-3xl p-8 sm:p-12" data-testid="quiz-section">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-cyan-500/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-teal-500/10 blur-3xl" />

      <div className="relative mx-auto max-w-2xl text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-500 shadow-lg shadow-cyan-500/30">
          <Wand2 className="h-7 w-7 text-slate-950" />
        </div>
        <h2 className="mt-6 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          Znajdź swoją <span className="gradient-text">następną książkę</span>
        </h2>
        <p className="mt-3 text-slate-400">Odpowiedz na 3 pytania — dobierzemy tytuły idealne dla Ciebie.</p>

        {!results && !loading && (
          <div className="mt-8" data-testid="quiz-step">
            <div className="mb-6 flex justify-center gap-2">
              {quiz.questions.map((_, i) => (
                <div key={i} className={`h-1.5 w-10 rounded-full transition-colors ${i <= step ? "bg-cyan-400" : "bg-slate-700"}`} />
              ))}
            </div>
            <h3 className="text-lg font-semibold text-slate-100">{current.question}</h3>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {current.options.map((o) => (
                <button
                  key={o.value}
                  data-testid={`quiz-option-${o.value}`}
                  onClick={() => choose(o.value)}
                  className="rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3.5 text-sm font-medium text-slate-200 transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 hover:border-cyan-400/60 hover:bg-cyan-500/10 hover:text-cyan-200"
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="mt-10 flex items-center justify-center gap-3 text-cyan-300" data-testid="quiz-loading">
            <Sparkles className="h-5 w-5 animate-spin" /> Dobieramy książki...
          </div>
        )}

        {results && (
          <div className="mt-10" data-testid="quiz-results">
            <div className="grid grid-cols-3 gap-4">
              {results.map((p) => (
                <Link key={p.id} to={`/produkt/${p.id}`} className="group">
                  <div className="transition-transform duration-300 group-hover:-translate-y-2">
                    <BookCover product={p} />
                  </div>
                  <div className="mt-2 text-xs font-semibold text-slate-200 group-hover:text-cyan-300">{p.title}</div>
                  <div className="text-[11px] text-cyan-400">{p.price.toFixed(2)} zł</div>
                </Link>
              ))}
            </div>
            <button onClick={reset} data-testid="quiz-restart-button" className="mt-8 rounded-full border border-cyan-500/40 px-6 py-2.5 text-sm font-medium text-cyan-300 transition-colors hover:bg-cyan-500/10">
              Zagraj jeszcze raz
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
